import { Link } from "react-router-dom";
import { Badge } from "@/components/atoms/Badge";
import { Tooltip } from "@/components/atoms/Tooltip";
import type { EmptyReason } from "./leadEmptyReasons";

interface EmptyCellProps {
  reason: EmptyReason | null;
  onRetrigger?: (e: React.MouseEvent) => void;
  retriggering?: boolean;
}

const ACTION_CLASS = "font-medium text-bridge-accent hover:underline disabled:opacity-50";

function EmptyCellHelp({ reason, onRetrigger, retriggering }: EmptyCellProps & { reason: EmptyReason }) {
  return (
    <div className="max-w-[260px] space-y-1.5 text-left text-xs leading-snug">
      <div className="font-semibold">{reason.label}</div>
      <p>{reason.tooltip}</p>
      {reason.steps?.length ? (
        <div>
          <div className="font-semibold">What to do</div>
          <ol className="list-decimal space-y-0.5 pl-4">
            {reason.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </div>
      ) : null}
      {reason.actions?.length ? (
        <div className="flex flex-wrap gap-x-3 gap-y-1 pt-0.5">
          {reason.actions.map((action) =>
            action.retrigger ? (
              onRetrigger && (
                <button
                  key={action.label}
                  type="button"
                  disabled={retriggering}
                  onClick={onRetrigger}
                  className={ACTION_CLASS}
                >
                  {action.label}
                </button>
              )
            ) : (
              <Link key={action.label} to={action.to ?? "/"} onClick={(e) => e.stopPropagation()} className={ACTION_CLASS}>
                {action.label}
              </Link>
            ),
          )}
        </div>
      ) : null}
    </div>
  );
}

export function EmptyCell({ reason, onRetrigger, retriggering }: EmptyCellProps) {
  if (!reason) return <span className="text-xs text-bridge-subtle">—</span>;
  return (
    <Tooltip side="top" hoverOnly content={<EmptyCellHelp reason={reason} onRetrigger={onRetrigger} retriggering={retriggering} />}>
      <span className="inline-flex max-w-full">
        <Badge tone={reason.tone} className="h-5 max-w-full truncate rounded-full px-2 text-[10px]">
          {reason.label}
        </Badge>
      </span>
    </Tooltip>
  );
}
